import { useCallback, useMemo } from 'react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';
import { FolderBusinessEntityListCmp_ThirdParty_Fragment } from '../../../components/FolderBusinessEntityList/fragments/third-party.fragment';
import { getThirdPartyName } from '../../../components/FolderBusinessEntityList/utils/third-party-name.util';

import { MissionForm_Mission_Fragment } from '../fragments/mission.fragment';
import type { MissionFormActor } from '../utils/quotation-case.util';

export const MissionForm_SubMissionActors_Collaboration_QueryDocument = graphql(
  `
    query MissionForm_SubMissionActors_Collaboration_Query($id: Id!) {
      findOneMission(id: $id) {
        _id
        consolidation {
          quotationEmitterActors {
            thirdPartyId
          }
          quotationReceiverActors {
            thirdPartyId
          }
          # The actors of the collaboration, its own or inherited from the folder.
          internalActors {
            ...MissionForm_QuotationCollaboration_Actor_Fragment
          }
          externalActors {
            ...MissionForm_QuotationCollaboration_Actor_Fragment
          }
        }
        ...MissionForm_Mission_Fragment
      }
    }
  `,
);

export const MissionForm_QuotationCollaboration_Actor_Fragment = graphql(`
  fragment MissionForm_QuotationCollaboration_Actor_Fragment on Actor {
    _id
    categoryIds
    index
    isInternal
    kind
    thirdPartyId
    typeId
    thirdParty {
      ...FolderBusinessEntityListCmp_ThirdParty_Fragment
    }
    ... on ActorOrganization {
      ...ActorForm_RepresentativesDetailsStep_ActorOrganization_Fragment
      useItsEdmDomain
      references {
        _id
        typeId
        value
      }
      representatives {
        contextualRoleIds
        index
        personId
        typeIds
      }
    }
    ... on ActorPerson {
      contextualRoleIds
      references {
        _id
        typeId
        value
      }
    }
    ...ActorForm_Actor_Fragment
  }
`);

export interface QuotationActorOption {
  readonly thirdPartyId: Id;
  readonly name: string;
}

// The collaboration of a sub-mission being created: its quotation emitter and
// receiver, and its internal actors able to quote its emitter (internal
// quotation), whether its actors are its own or inherited from the folder.
export const useQuotationCollaboration = (collaborationId: Id | null) => {
  const { data } = useGraphQLQuery(
    MissionForm_SubMissionActors_Collaboration_QueryDocument,
    { id: collaborationId ?? EMPTY_ID },
    { enabled: !!collaborationId },
  );

  const collaboration = data?.findOneMission;
  // Its own lists, null while they are inherited from the folder.
  const ownMission = getFragmentData(
    MissionForm_Mission_Fragment,
    collaboration,
  );
  const formLists = useMemo(
    () => ({
      internalActors: ownMission?.internalActors ?? null,
      externalActors: ownMission?.externalActors ?? null,
    }),
    [ownMission?.externalActors, ownMission?.internalActors],
  );
  const consolidatedInternalActors = getFragmentData(
    MissionForm_QuotationCollaboration_Actor_Fragment,
    collaboration?.consolidation.internalActors,
  );
  const consolidatedActors = useMemo(
    () =>
      collaboration
        ? [
            ...(consolidatedInternalActors ?? EMPTY_ARRAY),
            ...getFragmentData(
              MissionForm_QuotationCollaboration_Actor_Fragment,
              collaboration.consolidation.externalActors,
            ),
          ]
        : undefined,
    [collaboration, consolidatedInternalActors],
  );
  const emitterThirdPartyId =
    collaboration?.consolidation.quotationEmitterActors[0]?.thirdPartyId;
  const receiverThirdPartyId =
    collaboration?.consolidation.quotationReceiverActors[0]?.thirdPartyId;

  const formActorOf = useCallback(
    (thirdPartyId: Id | undefined): MissionFormActor | undefined =>
      consolidatedActors?.find((actor) => actor.thirdPartyId === thirdPartyId),
    [consolidatedActors],
  );

  const actorOptions = useMemo(
    () =>
      (consolidatedActors ?? EMPTY_ARRAY).map((actor) => ({
        thirdPartyId: actor.thirdPartyId,
        isInternal: actor.isInternal,
        name: getThirdPartyName(
          getFragmentData(
            FolderBusinessEntityListCmp_ThirdParty_Fragment,
            actor.thirdParty,
          ),
        ),
      })),
    [consolidatedActors],
  );

  // The internal actors of the collaboration other than its emitter.
  const quotationEmitterCandidates: readonly QuotationActorOption[] = useMemo(
    () =>
      actorOptions.filter(
        (option) =>
          option.thirdPartyId !== emitterThirdPartyId &&
          !!consolidatedInternalActors?.some(
            ({ thirdPartyId }) => thirdPartyId === option.thirdPartyId,
          ),
      ),
    [actorOptions, consolidatedInternalActors, emitterThirdPartyId],
  );

  return {
    actorOptions: actorOptions as readonly QuotationActorOption[],
    collaborationEmitter: formActorOf(emitterThirdPartyId),
    emitterThirdPartyId,
    formActorOf,
    formLists,
    // A sub-mission inherits the internal actors of its collaboration only when there is one.
    hasSeveralInternalActors: (consolidatedInternalActors?.length ?? 0) > 1,
    isLoaded: !!collaboration,
    quotationEmitterCandidates,
    receiverThirdPartyId,
  };
};
